import { useEffect } from "react";
import { api } from "../../state/api";
import type {
  CompareAppRate,
  ComparePlan,
  PlanResponse,
} from "../../state/api.types";
import type { ServiceTerritory, TermMonths } from "../PlanEditPage/common";

export type PlanValues = {
  termMonths: number | undefined;
  energyRateKwh: number | undefined;
  exportRateKwh: number | undefined;
  baseChargeMonth: number | undefined;
  deliveryChargeKwh: number | undefined;
  deliveryBaseMonth: number | undefined;
  renewableContentPercent: number | undefined;
  earlyTerminationFee: number | undefined;
  average500Kwh: number | undefined;
  average1000Kwh: number | undefined;
  average2000Kwh: number | undefined;
  monthlyCommission: number | undefined;
  evChargingFee: number | undefined;
  evChargingEnergyRateKwh: number | undefined;
  evChargingSchedule: string | undefined;
  evChargingHours: number | undefined;
  evChargingIncludesTdsp: boolean | undefined;
  evChargingAssumedChargingKwh: number | undefined;
  evChargingLimit: number | undefined;
  evChargingAdditionalInfo: string | undefined;
};

export function rateComparisonToValues(
  rate?: CompareAppRate,
  monthlyCommissionDollars?: string | null,
): PlanValues {
  const monthlyCommission =
    monthlyCommissionDollars !== null && monthlyCommissionDollars !== undefined
      ? parseFloat(monthlyCommissionDollars)
      : undefined;
  if (rate === undefined) {
    return {
      termMonths: undefined,
      energyRateKwh: undefined,
      exportRateKwh: undefined,
      baseChargeMonth: undefined,
      deliveryChargeKwh: undefined,
      deliveryBaseMonth: undefined,
      renewableContentPercent: undefined,
      earlyTerminationFee: undefined,
      average500Kwh: undefined,
      average1000Kwh: undefined,
      average2000Kwh: undefined,
      monthlyCommission,
      evChargingFee: undefined,
      evChargingEnergyRateKwh: undefined,
      evChargingSchedule: undefined,
      evChargingHours: undefined,
      evChargingIncludesTdsp: undefined,
      evChargingAssumedChargingKwh: undefined,
      evChargingLimit: undefined,
      evChargingAdditionalInfo: undefined,
    };
  }
  return {
    termMonths: rate.term_months,
    energyRateKwh: parseFloat(rate.energy_charge_kwh_dollars),
    exportRateKwh: parseFloat(rate.export_rate_kwh_dollars),
    baseChargeMonth: parseFloat(rate.base_charge_month_dollars),
    deliveryChargeKwh: parseFloat(rate.delivery_charge_kwh_dollars),
    deliveryBaseMonth: parseFloat(rate.delivery_base_month_dollars),
    renewableContentPercent: rate.renewable_content_percent ?? 0,
    earlyTerminationFee: parseFloat(rate.early_termination_fee_dollars),
    average500Kwh: parseFloat(rate.average_500_kwh_dollars),
    average1000Kwh: parseFloat(rate.average_1000_kwh_dollars),
    average2000Kwh: parseFloat(rate.average_2000_kwh_dollars),
    monthlyCommission,
    evChargingFee:
      rate.ev_charging_fee_dollars !== null &&
      rate.ev_charging_fee_dollars !== undefined
        ? parseFloat(rate.ev_charging_fee_dollars)
        : undefined,
    evChargingEnergyRateKwh:
      rate.ev_charging_energy_rate_kwh_dollars !== null &&
      rate.ev_charging_energy_rate_kwh_dollars !== undefined
        ? parseFloat(rate.ev_charging_energy_rate_kwh_dollars)
        : undefined,
    evChargingSchedule: rate.ev_charging_schedule ?? undefined,
    evChargingHours:
      rate.ev_charging_hours !== null && rate.ev_charging_hours !== undefined
        ? parseFloat(rate.ev_charging_hours)
        : undefined,
    evChargingIncludesTdsp: rate.ev_charging_includes_tdsp ?? undefined,
    evChargingAssumedChargingKwh:
      rate.ev_charging_assumed_charging_kwh !== null &&
      rate.ev_charging_assumed_charging_kwh !== undefined
        ? parseFloat(rate.ev_charging_assumed_charging_kwh)
        : undefined,
    evChargingLimit:
      rate.ev_charging_limit !== null && rate.ev_charging_limit !== undefined
        ? parseFloat(rate.ev_charging_limit)
        : undefined,
    evChargingAdditionalInfo: rate.ev_charging_additional_info ?? undefined,
  };
}

export function usePlanComparison(
  plan: PlanResponse,
  serviceTerritory: ServiceTerritory,
  termMonths: TermMonths,
) {
  const [comparePlan, comparePlanState] = api.useComparePlanMutation();
  useEffect(() => {
    comparePlan({
      planUUID: plan.uuid,
      term_months: parseInt(termMonths),
      service_territory: serviceTerritory,
      export_rate_kwh_dollars: plan.export_rate_kwh_dollars ?? "0",
      early_termination_fee_dollars: plan.early_termination_fee_dollars,
      plan_monthly_cost_dollars: plan.plan_monthly_cost_dollars ?? "0",
      monthly_commission_dollars:
        plan.partner_commission_per_month_dollars ?? "0",
    });
  }, [
    comparePlan,
    plan.uuid,
    termMonths,
    serviceTerritory,
    plan.export_rate_kwh_dollars,
    plan.early_termination_fee_dollars,
    plan.plan_monthly_cost_dollars,
    plan.partner_commission_per_month_dollars,
  ]);
  return comparePlanState;
}

function comparisonKey(comparison: ComparePlan) {
  return `${comparison.provider}-${comparison.plan}`;
}

function usePlanComparisonsByKey(
  planComparison: ReturnType<typeof usePlanComparison>,
) {
  if (!planComparison.data) {
    return {};
  }
  return Object.fromEntries(
    planComparison.data.comparisons.map((comparison) => [
      comparisonKey(comparison),
      comparison,
    ]),
  );
}

export function usePlanComparisonValues(
  planComparison: ReturnType<typeof usePlanComparison>,
) {
  const planComparisonsByKey = usePlanComparisonsByKey(planComparison);
  if (!planComparison.data) {
    return [];
  }
  return planComparison.data.comparison_plans.map((comparisonPlan) => {
    const key = comparisonKey(comparisonPlan);
    return rateComparisonToValues(planComparisonsByKey[key]);
  });
}

export const SERVICE_TERRITORY_SHORT = {
  ONCOR: "Oncor",
  CENTERPOINT: "CenterPoint",
  CenterPoint: "CenterPoint",
  AEP_NORTH: "AEP North",
  AEP_CENTRAL: "AEP Central",
  TNMP: "TNMP",
  LUBBOCK: "Lubbock",
};
